import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, TemplateRef, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  ActionIconDef, Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridRowCommitEvent, GridRowEditStartEvent,
  GridToolbarActionClickEvent,
} from '@inavista/components/grid';
import { IInventoryGroupType, IReceiptsExceptionsTypes, shipmentOrderStatuses } from 'projects/service/src/app/modules/home/models/orders.model';
import { InventoryService } from 'projects/service/src/app/modules/home/services/inventory.service';
import { ApiResponse } from 'projects/service/src/app/shared/models/app.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { CellEdit, CheckboxEditor, DropdownEditor, NumberEditor, TextEditor } from 'projects/service/src/app/shared/utils/cell-editors';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ADD_EXCEPTION_ACTION, buildShippedExceptionsActionIcons, buildShippedExceptionsGridConfig, DELETE_EXCEPTION_ACTION,
  SHIPPED_EXCEPTIONS_COLUMNS, SHIPPED_EXCEPTIONS_EDIT_FIELDS, SHIPPED_EXCEPTIONS_OPEN_FIELD, ShippedExceptionsGridContext,
} from './shipped-exceptions.grid.config';

// The old grid had no height (its gridHeight was never given a value) and showed every row. No paging now (Martin):
// the grid is as tall as its rows, up to 20, then scrolls. Toolbar + header + horizontal scrollbar, and the row height.
const GRID_CHROME_PX = 92;
const ROW_PX = 30;
const MAX_VISIBLE_ROWS = 20;
const MIN_VISIBLE_ROWS = 2;

@Component({
    selector: 'app-shipped-exceptions',
    templateUrl: './shipped-exceptions.component.html',
    styleUrls: ['./shipped-exceptions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads this.exceptions, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class ShippedExceptionsComponent implements OnInit, OnChanges {

  @Input() exceptions : any = [];
  @Input() status : string = shipmentOrderStatuses.shipped;
  @Input() shipmentOrderId: any;
  @Input() shipmentOrderData: any;

  gridHeight = GRID_CHROME_PX + MIN_VISIBLE_ROWS * ROW_PX;
  allowEditing: boolean = true;
  gridConfig: GridConfig = null;
  actionIcons: ActionIconDef[] = [];
  shipmentOrderExceptionsGrid: InavistaGrid;
  exceptionsDDL: any = [];
  itemsList: any = [];

  @Output() onExceptionGridUpdated = new EventEmitter();

  @ViewChild('shipmentOrderExceptionsGrid') set gridComponent(gridComponent: InavistaGrid) {
    this.shipmentOrderExceptionsGrid = gridComponent;
  }
  @ViewChild('returnCell', { static: true }) returnCell: TemplateRef<unknown>;
  @ViewChild('reasonEditor', { static: true }) reasonEditor: TemplateRef<unknown>;
  @ViewChild('itemEditor', { static: true }) itemEditor: TemplateRef<unknown>;
  @ViewChild('descriptionEditor', { static: true }) descriptionEditor: TemplateRef<unknown>;
  @ViewChild('quantityEditor', { static: true }) quantityEditor: TemplateRef<unknown>;
  @ViewChild('typeEditor', { static: true }) typeEditor: TemplateRef<unknown>;
  @ViewChild('returnEditor', { static: true }) returnEditor: TemplateRef<unknown>;
  exceptionTypeObject: DropdownEditor;
  itemReturnedObject: CheckboxEditor;
  isTabValid: boolean = false;
  selectedRowId: any;
  itemCodeObject: DropdownEditor;
  onItemUpdate = new Subject<string>();
  selectItemCode: any;
  showItemAdvancedSearchPopup: boolean = false;
  itemChangedFromAdvanceSearch: boolean = false;
  itemDescriptionObject: TextEditor;
  itemTypeObject: TextEditor;
  itemQuantityObject: NumberEditor;
  serviceItemGroup: any;
  roleCanEdit: any;
  canEdit: any;

  // Each editable column's editor (CellEditorHost), and the editors open in the row being edited.
  readonly edits: Record<string, CellEdit> = {
    reasonDescription: this.editExceptionTypes(),
    itemCode: this.editItem(),
    itemDescription: this.editItemDescription(),
    quantity: this.editItemQuantity(),
    type: this.editItemType(),
    isReturn: this.editItemReturned(),
  };
  readonly openEditors = new Map<string, CellEdit>();
  // The row open in the grid (the old grid.isEdit), a new one Add Exception opened, and the open row's hidden columns.
  isEditing = false;
  private pendingNewException: any = null;
  hiddenEditRow: any = null;
  hiddenEditFields: string[] = [];
  private readonly gridRows = inject(LocalRowsGridApi);

  constructor(
    private commonService: CommonService,
    private inventoryService: InventoryService,
    private dialog: MatDialog,
  ) { }

  ngOnChanges(change) {
    if(change?.status){
      this.status === change.status.currentValue;
      this.getCanEditValue();
      // this.enableDisableButton();
    }

    if (change?.exceptions && !change.exceptions.firstChange) {
      // Fixed (Martin, 2026-10-28): a new list from the popup kept no row ids (only ngOnInit set them), so its rows could
      // not be told apart for editing; the rows without one are numbered after the others, as ngOnInit does.
      let next = this.commonService.getMaxTempRowId((this.exceptions ?? []).filter(r => r.tempRowId != null)) + 1;
      (this.exceptions ?? []).forEach(row => { if (row.tempRowId == null) row.tempRowId = next++; });
      this.refreshRows();
    }

    if(change?.exceptions && this.status === shipmentOrderStatuses.deliveryAcknowledged){
      if(!this.checkEmptyRows() && this.canEdit){
          this.addEmptyRowsToItemsData();
      }
    }
  }

  ngOnInit(): void {
    this.inventoryService.receiptsPopupLoader.next(true);
    this.getCanEditValue();
    this.exceptions.map((row, index) => {
      row.tempRowId = index;
      row.item = `${row.itemCode} | ${row.itemDescription}`
    });
    const ctx: ShippedExceptionsGridContext = {
      editors: {
        reasonDescription: this.reasonEditor, itemCode: this.itemEditor, itemDescription: this.descriptionEditor,
        quantity: this.quantityEditor, type: this.typeEditor, isReturn: this.returnEditor,
      },
      returnCell: this.returnCell,
      canAdd: !!this.commonService.roleClaims['PO_Popup_ItemTabGrid_Toolbar_Add_Item']?.visible,
      canEdit: () => !!this.allowEditing,
      deleteLockReason: () => this.status === shipmentOrderStatuses.deliveryAcknowledged
        ? null : 'Exceptions can be deleted while the order is Delivery Acknowledged.',
    };
    this.gridRows.columns.set(LocalRowsGridApi.schema(SHIPPED_EXCEPTIONS_COLUMNS));
    this.gridConfig = buildShippedExceptionsGridConfig(ctx);
    this.actionIcons = buildShippedExceptionsActionIcons(ctx);
    this.refreshRows();
    this.getExceptionsDDL();
    this.getItemList();
    this.onItemUpdate.pipe(
          debounceTime(500),
          distinctUntilChanged()
      ).subscribe(value => {
          if (this.itemCodeObject) {
              this.itemCodeObject.showPopup();
              this.itemCodeObject.showSpinner();
          }
          this.getItemList({itemCode: value});
      });
  }

  // The rows the grid shows: the exceptions as the page holds them (deleted ones too, as before).
  refreshRows() {
    const exceptions = Array.isArray(this.exceptions) ? this.exceptions : [];
    this.gridRows.rows.set(exceptions.map(row => ({ ...row })));
    this.gridHeight = GRID_CHROME_PX + Math.min(Math.max(exceptions.length, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS) * ROW_PX;
    this.shipmentOrderExceptionsGrid?.refresh();
  }

  /** The page's own exception a grid row stands for. */
  exceptionFor(row: any) {
    return (this.exceptions ?? []).find(r => r.tempRowId === row?.tempRowId) ?? row;
  }

  /** The exception an open grid row edits (a new one: the one Add Exception built). */
  editRowFor(row: any) {
    return row?.tempRowId == null ? this.pendingNewException : this.exceptionFor(row);
  }

  getCanEditValue(){
    this.roleCanEdit = this.commonService.roleClaims['Inv_Order_Receipt_Popup_Receipt_Tab']?.edit
    this.canEdit = this.status === shipmentOrderStatuses.deliveryAcknowledged && this.roleCanEdit;
    this.allowEditing = this.canEdit;
    // The lock changed: rows open only while the user may change them.
    if (this.gridConfig) this.gridConfig = { ...this.gridConfig, editMode: this.allowEditing ? 'inline' : 'none' };
  }

  getExceptionsDDL(){
    this.inventoryService.getReasonOrExceptionCodesForDDL('ShipmentOrder')
    .subscribe((res: any) => {
      if(res.isSuccessful){
        this.exceptionsDDL = res.result.filter(row => [IReceiptsExceptionsTypes.damaged, IReceiptsExceptionsTypes.unknown, IReceiptsExceptionsTypes.wrongpart].includes(row.text));
        // this.showLoader = false;
        if(this.canEdit){
          this.addEmptyRowsToItemsData();
        }
      } else {
        this.commonService.showNotification('error', res.message);
        // this.showLoader = false;
      }
    }, (error: HttpErrorResponse) => {
      // this.showLoader = false;
      throw error;
    });
  }

   addEmptyRowsToItemsData() {
    for (let i = 0; i < 2; i++) {
      this.exceptions?.push(this.setExceptionRow({}));
    }
    this.refreshRows();
    setTimeout(() => {
      this.inventoryService.receiptsPopupLoader.next(false);
    }, 100);
  }

  // As before: until the grid exists, the list counts as having no empty row.
  checkEmptyRows(){
    if (!this.shipmentOrderExceptionsGrid) return undefined;
    const hasEmptyRow = this.exceptions?.some(row => !row.reasonCode && !row.itemCode && !row.quantity);
    return hasEmptyRow;
  }

  // The row ids come from the rows the grid shows (none until it exists), as before.
  setExceptionRow(detail) {
    const rows = this.shipmentOrderExceptionsGrid ? (this.exceptions || []) : [];
    detail.tempRowId = this.commonService.getMaxTempRowId(rows) + 1;
    detail.rowId = this.commonService.getMaxRowId(rows) + 1;
    detail.shoExceptionId = 0,
    detail.shipmentOrderId = this.shipmentOrderId;
    detail.adjustmentOrderId = '';
    detail.adjustmentOrderLineId = null;
    detail.itemCode = '';
    detail.itemDescription = '';
    detail.itemGroup = '';
    detail.type = '';
    detail.quantity = null;
    detail.quantitySent = null;
    detail.quantityReceived = null;
    detail.reasonCode = null;
    detail.reasonDescription = null;
    detail.isDeleted = false;
    detail.isAdjustmentOrder = false;
    detail.isManual = false;
    detail.isRemovedFromExceptionList = false;
    detail.orderReceived = true;
    detail.exceptionStatus = '';
    detail.technicianId = '';
    detail.technicianName = '';
    detail.shipmentOrderLN = '';
    detail.status = '';
    detail.warehouseOrderLN = '';
    detail.adjustmentDate = null;
    detail.reasonForAdjustment = '';
    detail.warehouse = '';
    detail.inventoryDate = null;
    detail.fromLoc = '';
    detail.line = detail.rowId;
    detail.inventoryAdjustedStorageUnit = null;
    detail.inventoryAdjustedInventoryUnit = null;
    detail.varianceStorageUnit = null;
    detail.createdBy = '';
    detail.createdDate = null;
    detail.createdByName = '';
    detail.lastUpdatedBy = '';
    detail.lastUpdatedDate = null;
    detail.lastUpdatedByName = '';
    detail.isReturn = false;
    return detail;
  }

    showMasterItemCodePopup(eqData: any) {
        if (!eqData?.itemCode) return;
        const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
        panelClass: 'master-itemcode-popup-dialog',
        width: '80%',
        height: '80%',
        data: eqData
        });
        dialogRef.afterClosed().subscribe((result) => { });

    }

  getItemList(rowData?) {
    const value = rowData?.itemCode || '';
    let serviceItemGroup = '';
    this.commonService.getItems(value, 10, this.commonService.USER.company, serviceItemGroup)
    .subscribe((res: ApiResponse) => {
      if(res && res.result && res.result.length) {
          res.result.map((item, index) => {
            item.value =`${item.item}`
            item.text =`${item.item} | ${item.itemDescription}`
          });
          this.itemsList = res.result;
          this.itemCodeObject ? this.itemCodeObject.dataSource = res.result : null;
          // this.showGrid = true;
      } else {
          this.itemsList = [];
      }
    });
  }

  // Add Exception: refused while an empty row is there or a row is open, as before; otherwise a new row opens.
  onToolbarActionClick(event: GridToolbarActionClickEvent) {
    if (event.key !== ADD_EXCEPTION_ACTION || !this.allowEditing) return;
    this.inventoryService.receiptsPopupLoader.next(true);
    if(this.exceptions.filter(row => !row.reasonCode && !row.itemCode && !row.quantity).length > 0){
      this.inventoryService.receiptsPopupLoader.next(false);
      this.commonService.showNotification('warning', 'There is already empty row present');
    }
    else{
      if ( this.isEditing ) {
        this.inventoryService.receiptsPopupLoader.next(false);
        this.commonService.showNotification('warning', 'There is a row in exception grid, which is already in edit mode!', 'center', 5000);
      } else {
        this.pendingNewException = this.setExceptionRow({});
        this.shipmentOrderExceptionsGrid?.startDraft();
        this.inventoryService.receiptsPopupLoader.next(false);
      }
    }
  }

  // The row opened: its editors draw, then the old actionComplete('beginEdit').
  onRowEditStart(event: GridRowEditStartEvent) {
    const rowData = this.editRowFor(event.row);
    if (!rowData) return;
    this.isEditing = true;
    this.selectedRowId = rowData.tempRowId;
    // Quantity and Type can be hidden; the other editors read theirs, so they are drawn out of sight.
    const shown = this.shipmentOrderExceptionsGrid?.visibleColumnIds() ?? SHIPPED_EXCEPTIONS_EDIT_FIELDS;
    this.hiddenEditFields = SHIPPED_EXCEPTIONS_EDIT_FIELDS.filter(f => !shown.includes(f));
    this.hiddenEditRow = this.hiddenEditFields.length ? rowData : null;
    // Leaving the row always saves it, as the Syncfusion grid did: the row counts as changed from the start.
    const marked = this.shipmentOrderExceptionsGrid?.setDraftValue(SHIPPED_EXCEPTIONS_OPEN_FIELD, String(Date.now()));
    setTimeout(() => {
      if (!marked) this.shipmentOrderExceptionsGrid?.setDraftValue(SHIPPED_EXCEPTIONS_OPEN_FIELD, String(Date.now()));
      if ( rowData.reasonDescription === 'Unknown' && this.itemCodeObject ) {
        this.itemCodeObject.dataSource = [this.commonService.unknownPart];
      }
    });
  }

  // Enter, the ✓ icon or a click on another row: every editor's value goes into the row, as the Syncfusion grid did,
  // then the old actionComplete('save').
  onRowCommit(commit: GridRowCommitEvent) {
    const target = this.editRowFor(commit.row);
    if (!target) return;
    for (const [field, edit] of this.openEditors) target[field] = edit.read();
    if (commit.isDraft) this.exceptions.push(target);
    this.pendingNewException = null;
    this.hiddenEditRow = null;
    this.isEditing = false;
    const reason = this.exceptionsDDL.find( row => row.text === target.reasonDescription);
    // Fixed (Martin, 2026-10-28): saving a row with no exception threw here (reading .value of nothing) and stopped;
    // it now stops the same way without the error.
    if (reason) {
      target.reasonCode = reason.value;
      target.serviceItemGroup = this.serviceItemGroup;
      const emptyRecordsList = this.exceptions.filter((item, index) => !item.reasonDescription );
      if (emptyRecordsList?.length < 2) {
        this.addExceptionNewRow();
        // Then the first empty row opens for editing, as the old grid did; not over a row the user opened meanwhile.
        const empty = this.exceptions.find(item => !item.reasonDescription);
        if (empty) setTimeout(() => { if (!this.isEditing) this.shipmentOrderExceptionsGrid?.startEdit(empty.tempRowId); });
      }
    }
    this.refreshRows();
  }

  // Esc or ✕: the row stays as it was (Syncfusion's cancel); a new row is not added.
  onRowRevert(event: { row: any; isDraft: boolean }) {
    this.pendingNewException = null;
    this.hiddenEditRow = null;
    this.isEditing = false;
  }

  addExceptionNewRow() {
    let newException = this.setExceptionRow({});
    this.exceptions.push(newException);
    this.refreshRows();
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey !== DELETE_EXCEPTION_ACTION || this.status !== shipmentOrderStatuses.deliveryAcknowledged) return;
    const row = this.exceptionFor(event.row);
    if(!row.shoExceptionId){
      const index = this.exceptions.findIndex(x => x.tempRowId === row.tempRowId);
      this.exceptions.splice(index, 1)
      this.refreshRows();
    } else {
      this.commonService.showConfirmation('Are you sure? You want to delete this exception?').then(result => {
        if (result.isConfirmed) {
          row.isDeleted = true;
          this.refreshRows();
        }
      })
    }
  }

  editExceptionTypes(): CellEdit {
      return {
          read: () => this.exceptionTypeObject?.value,
          destroy: () => {
            if ( this.exceptionTypeObject ) {
              this.exceptionTypeObject.destroy();
              this.exceptionTypeObject = null;
            }
          },
          open: args => {
              return this.exceptionTypeObject = new DropdownEditor({
                  dataSource: this.exceptionsDDL.filter(row => [IReceiptsExceptionsTypes.damaged, IReceiptsExceptionsTypes.unknown, IReceiptsExceptionsTypes.wrongpart].includes(row.text)),
                  fields: { value: 'text', text: 'text' },
                  popupWidth: '150px',
                  value: args.rowData.reasonDescription,
                  change: event => {
                    // this.reasonCode = event.itemData.value;
                    // this.reasonDescription = event.itemData.text;
                    this.itemCodeObject.value = this.itemDescriptionObject.value = null;
                    this.itemReturnedObject.checked = true;
                    if(event.itemData.text == IReceiptsExceptionsTypes.unknown){
                      this.itemCodeObject.dataSource = [this.commonService.unknownPart];
                      this.itemCodeObject.value = this.selectItemCode = this.commonService.unknownPart.value;
                      this.itemDescriptionObject.value = this.commonService.unknownPart.text;
                      this.itemCodeObject.enabled = false;
                      this.itemCodeObject.actionVisible.set(false);
                    } else {
                      this.itemCodeObject.dataSource = this.itemsList;
                      this.itemCodeObject.value = null;
                      this.itemCodeObject.enabled = true;
                      this.itemCodeObject.actionVisible.set(true);
                    }
                  }
              });
          }
      }
    }

  editItem(): CellEdit {
    return {
      read: () => this.itemCodeObject?.value,
      destroy: () => this.itemCodeObject?.destroy(),
      open: args => {
        this.selectedRowId = args.rowData?.tempRowId;
        this.selectItemCode = args.rowData.itemCode;
        // Fixed (Martin, 2026-10-28): the Exception editor is read when it is already drawn (the Syncfusion grid drew it
        // first); a user who moved Item before Exception would have had no editor yet, so the row's own value is used.
        const exceptionType = this.exceptionTypeObject ? this.exceptionTypeObject.value : args.rowData.reasonDescription;
        this.itemCodeObject = new DropdownEditor({
            dataSource: this.itemsList,
            fields: { value: 'value', text: 'text' },
            value: args.rowData.itemCode,
            allowFiltering: true,
            showClearButton: true,
            popupWidth: '350px',
            openOnFocus: true,
            enabled: [IReceiptsExceptionsTypes.damaged, IReceiptsExceptionsTypes.wrongpart].includes(exceptionType),
            filtering: args => { this.onItemUpdate.next(args.text) },
            action: { title: 'Advanced Search', iconClass: 'fas fa-info', click: () => this.showItemPopup() },
            focus: () => {
              if(args.rowData.reasonCode == this.exceptionsDDL.find(row => [IReceiptsExceptionsTypes.unknown].includes(row.text)).value){
                this.itemCodeObject.dataSource = [this.commonService.unknownPart];
              } else {
                const existingItems = [];
                this.exceptions?.forEach(exception => {
                    if (exception.itemCode !== this.itemCodeObject?.value) {
                        existingItems.push(exception.itemCode);
                    }
                });
                this.itemCodeObject.dataSource = this.itemsList.filter(item => !existingItems.includes(item.value));
              }
            },
            change: event => {
                if ( event.value && event.itemData ) {
                  this.itemDescriptionObject.value = event.itemData?.itemDescription ? event.itemData.itemDescription : null;
                //   this.itemCodeObject.value = event.itemData?.item;
                  this.serviceItemGroup = event.itemData?.serviceItemGroup;
                  this.itemTypeObject.value = this.serviceItemGroup === this.commonService.coreItemServiceGroup ? IInventoryGroupType.core: IInventoryGroupType.standard;
                  let damagedExceptionCode = this.exceptionsDDL.find(row => [IReceiptsExceptionsTypes.damaged].includes(row.text));
                  if(this.exceptionTypeObject.value === damagedExceptionCode.text){
                    if(this.serviceItemGroup === this.commonService.coreItemServiceGroup){
                      this.itemReturnedObject.checked = true;
                    } else {
                      this.itemReturnedObject.checked = false;
                    }
                  }

                //   this.itemGroup = event.itemData?.itemGroup;
                  this.itemChangedFromAdvanceSearch = false;
                //   this.exceptionQuantityObject.value = 1
                }
            }
        });
        // The Advanced Search button shows for Damaged and Wrong Part, as before.
        this.itemCodeObject.actionVisible.set([IReceiptsExceptionsTypes.damaged, IReceiptsExceptionsTypes.wrongpart].includes(args.rowData.reasonDescription));
        return this.itemCodeObject;
      }
    }
  }

  showItemPopup() {
    this.showItemAdvancedSearchPopup = true;
    this.inventoryService.receiptsPopupLoader.next(true);
  }

  // The row stays open while the advanced search is up (the grid never saves on losing focus), so the item picked
  // goes into the open row's editors, as before.
  onItemAdvancedSearchPopupClose(data: any) {
    this.itemChangedFromAdvanceSearch = true;
    if ( this.isEditing && data && this.itemCodeObject ) {
      this.itemDescriptionObject.value = data?.itemDescription;
      this.itemCodeObject.dataSource = [{
          text: `${data?.item} | ${data?.itemDescription}`,
          value: data?.item,
          ...data
      }];
      this.itemCodeObject.value = data?.item;
    }
    setTimeout(() => {
        this.showItemAdvancedSearchPopup = false;
        this.inventoryService.receiptsPopupLoader.next(false);
    }, 100);
  }

  editItemDescription(): CellEdit {
    return {
        read: () => this.itemDescriptionObject?.value,
        destroy: () => {
            this.itemDescriptionObject?.destroy();
            this.itemDescriptionObject = null;
        },
        open: args => {
            return this.itemDescriptionObject = new TextEditor({
                value: args.rowData.itemDescription,
                enabled: false,
            });
        }
    }
  }

  // Quantity: the numeric editor the old column used (editType numericedit, its params).
  editItemQuantity(): CellEdit {
    return {
        read: () => this.itemQuantityObject?.value,
        destroy: () => {
            this.itemQuantityObject?.destroy();
            this.itemQuantityObject = null;
        },
        open: args => {
            return this.itemQuantityObject = new NumberEditor({
                value: args.rowData.quantity,
                decimals: 0,
                min: 1,
                format: 'N',
            });
        }
    }
  }

  editItemType(): CellEdit {
    return {
        read: () => this.itemTypeObject?.value,
        destroy: () => {
            this.itemTypeObject?.destroy();
            this.itemTypeObject = null;
        },
        open: args => {
            return this.itemTypeObject = new TextEditor({
                value: args.rowData.type,
                enabled: false,
            });
        }
    }
  }

  editItemReturned(): CellEdit {
    return {
      read: () => this.itemReturnedObject?.checked,
      destroy: () => {
          this.itemReturnedObject?.destroy();
          this.itemReturnedObject = null;
      },
      open: args => {
          return this.itemReturnedObject = new CheckboxEditor({
            value: args.rowData.isReturn,
            enabled: false,
          });
      }
    }
  }

  getExceptionsData(){
    return {
      exceptionList: this.exceptions.filter(row => {
        if(row.reasonCode && row.itemCode && row.quantity){
          row.warehouseOrderLN = row.warehouseOrderLN || this.itemsList[0].partsOrderRef;
          return row;
        }
      })
    }
  }

  onCheckboxClicked(event, data) {
    const rowIndex = this.exceptions.findIndex(col => col.tempRowId === data.tempRowId);
    let damagedExceptionCode = this.exceptionsDDL.find(row => [IReceiptsExceptionsTypes.damaged].includes(row.text));
    if(this.exceptions[rowIndex].reasonCode === damagedExceptionCode.value && this.exceptions[rowIndex].serviceItemGroup === this.commonService.coreItemServiceGroup && !event.checked){
      this.commonService.showNotification('warning', 'Core return items may only be discarded from your inventory when creating a damaged return order.');
    }
    this.exceptions[rowIndex]['isReturn'] = !data.isReturn;
    this.refreshRows();
  }

  validate(skipValidation){
    this.isTabValid = this.exceptions
                      .filter(row =>
                        (row.reasonCode && !row.itemCode) ||
                        (!row.reasonCode && row.itemCode) ||
                        (row.reasonCode && row.itemCode && row.quantity <= 0)
                      ).length <= 0;
    return this.isTabValid;
  }
}
